<template>
  <v-row justify="end" class="mr-12">
    <v-dialog
      v-model="dialog"
      persistent
      width="1024"
      @click:outside="dialog = false"
    >
      <template #activator="{ props }">
        <v-btn
          color="#004984"
          v-bind="props"
          class="text-capitalize text-white button-config"
        >
          Cadastrar Bug
          <v-icon right class="ml-1">mdi-plus</v-icon>
        </v-btn>
      </template>

      <v-card>
        <v-card-title>
          <div class="d-flex justify-space-between">
            <span class="text-h5">Cadastrar Bug</span>
            <v-btn
              variant="plain"
              rounded="xl"
              prepend-icon="mdi-window-close"
              @click="dialog = false"
            ></v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-form ref="form" validate-on="lazy submit">
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="descricao"
                    required
                    label="Descrição*"
                    placeholder="Descrição do bug encontrado"
                    rows="3"
                    :rules="descricaoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="observacao"
                    required
                    label="Observação*"
                    placeholder="Informações adicionais sobre o bug"
                    rows="3"
                    :rules="observacaoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-select
                    v-model="tipo_bug"
                    :items="['CORRECAO', 'MELHORIA']"
                    label="Tipo*"
                    required
                    :rules="tipoBugRules"
                  ></v-select>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-select
                    v-model="gravidade"
                    :items="[
                      'BLOQUEADORA',
                      'CRITICA',
                      'MEDIA',
                      'BAIXA',
                      'TRIVIAL',
                    ]"
                    label="Gravidade*"
                    required
                    :rules="gravidadeRules"
                  ></v-select>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-file-input
                    accept="image/*"
                    v-model="imagens"
                    multiple
                    label="Insira uma Imagem, Aceita apenas .png"
                  ></v-file-input>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-file-input
                    accept="video/*"
                    v-model="videos"
                    multiple
                    label="Insira um Video, Aceita apenas .mp4"
                  ></v-file-input>
                </v-col>

                <v-col cols="6">
                  <v-select
                    v-model="tarefa"
                    label="Tarefa*"
                    required
                    :items="tarefas.map((item) => item.titulo)"
                    :rules="tarefaRules"
                  ></v-select>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
          <small>*Campos obrigatórios</small>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="blue-darken-1" variant="text" @click="submitForm">
            Salvar
            <v-progress-circular
              v-if="this.isLoading"
              class="ml-2"
              indeterminate
              size="20"
            />
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
/**
 * @module ModalCriarBug
 * @desc Modal para criar um novo bug relacionado a um determinado
 * caso de uso.
 *
 * @vue-data {Number} [csuID=0] O id do caso de uso com o qual o bug criado
 * será relacionado
 * @vue-data {Boolean} [dialog=false] Flag que indica se o modal está visível
 * @vue-data {String} [descricao=''] Campo que armazena a descrição do bug
 * @vue-data {String} [observacao=''] Campo que armazena a observação
 * para o bug
 * @vue-data {String} [tipo_bug=''] Campo que armazena o tipo do bug
 * @vue-data {String} [gravidade=''] Campo que armazena a gravidade do bug
 * @vue-data {Boolean} [isLoading=false] Flag que indica se o bug está
 * sendo criado
 * @vue-data {string} [userID=''] - O id do usuário atualmente logado
 * @vue-data {string} [idProjeto=''] - O id do projeto atual
 * @vue-data {string} [tarefa=''] - Nome da tarefa selecionada
 * @vue-data {Array.<Object>} [tarefas=[]] - Array com as tarefas do
 * tipo TESTE_EQUIPE presentes no projeto.
 */
import axios from 'axios';

import store from '@/store/store';
export default {
  emits: ['bugCriado'],
  data: () => ({
    csuID: 0,
    dialog: false,
    descricao: '',
    observacao: '',
    // eslint-disable-next-line camelcase
    tipo_bug: '',
    gravidade: '',
    imagens: [],
    videos: [],
    isLoading: false,
    tarefa: '',
    tarefas: [],
    userID: '',
    idProjeto: '',
    descricaoRules: [(v) => !!v || 'Descrição é obrigatória'],
    observacaoRules: [
      (v) => !!v || 'Observação é obrigatória',
      (v) =>
        (v && v.length <= 100) ||
        'Observação deve ter no máximo 500 caracteres',
    ],
    tipoBugRules: [(v) => !!v || 'Tipo do bug é obrigatório'],
    gravidadeRules: [(v) => !!v || 'Gravidade é obrigatória'],
    tarefaRules: [(v) => !!v || 'Tarefa é obrigatória'],
  }),
  created() {
    this.csuID = this.$route.params.id;
    this.userID = localStorage.getItem('idUsuario');
    this.idProjeto = this.$route.params.idProjeto;
    this.fetchTarefas();
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    /**
     * Lida com o evento de pressionar tecla. Se a tecla pressionada for ESC,
     * o modal é fechado.
     *
     * @param {Object} event Objeto do evento
     */
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.dialog = false;
      }
    },

    /**
     * Realiza uma requisição HTTP para obter as tarefas de um projeto.
     *
     * @param {String} idProject O id do projeto.
     */
    fetchTarefas() {
      const apiUrl = process.env.VUE_APP_API_URL;

      axios
        .get(`${apiUrl}/tarefas/listar/${this.idProjeto}`, {
          headers: {
            Authorization: `Bearer ${store.state.token}`,
          },
        })
        .then((res) => {
          this.tarefas = res.data.tarefas.filter(
            (tarefa) => tarefa.tipo_tarefa === 'TESTE_EQUIPE',
          );
          console.log(this.tarefas);
        })
        .catch((error) => console.log(error));
    },

    /**
     * Função assíncrona que envia um formulário de relatório
     *  de bug para a API e atualiza a página
     *
     * @async
     * @function submitForm
     * @return {Promise<void>}
     *
     * @throws {Error} Se ocorrer um erro ao enviar
     *  o formulário ou atualizar a página
     *
     * @description A função envia um objeto de dados para
     * a API em formato JSON que contém a descrição e observações
     * do bug relatado, bem como seu tipo e gravidade. Em seguida, a
     * página é atualizada para mostrar o novo bug criado.
     */
    async submitForm() {
      const isValid = (await this.$refs.form.validate()).valid;

      if (isValid) {
        const tarefaSelecionada = this.tarefas.filter(
          (tarefa) => tarefa.titulo === this.tarefa,
        )[0];

        const data = {
          // eslint-disable-next-line camelcase
          id_tarefa: Number(tarefaSelecionada.id_tarefa),
          descricao: this.descricao,
          observacao: this.observacao,
          // eslint-disable-next-line camelcase
          tipo_bug: this.tipo_bug,
          // eslint-disable-next-line camelcase
          gravidade: this.gravidade,
          // eslint-disable-next-line camelcase
          id_usuario: Number(this.userID),
        };

        try {
          this.isLoading = true;
          const apiUrl = process.env.VUE_APP_API_URL;

          const formDataImagem = new FormData();
          const formDataVideo = new FormData();

          if (this.imagens.length > 0) {
            for (const imagem of this.imagens) {
              formDataImagem.append('files', imagem);
            }
          }

          if (this.videos.length > 0) {
            for (const video of this.videos) {
              formDataVideo.append('files', video);
            }
          }

          const response = await axios.post(
            `${apiUrl}/api/v1/bug/csu/${this.csuID}`,
            data,
            {
              headers: {
                Authorization: `Bearer ${store.state.token}`,
              },
            },
          );

          if (this.imagens.length > 0) {
            await axios({
              method: 'post',
              url: `${apiUrl}/api/v1/bug/arquivo/${response.data.bug.id_bug}`,
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              data: formDataImagem,
            });
          }

          if (this.videos.length > 0) {
            await axios({
              method: 'post',
              url: `${apiUrl}/api/v1/bug/arquivo/${response.data.bug.id_bug}`,
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              data: formDataVideo,
            });
          }

          const idProjeto = this.$route.params.idProjeto;

          this.$toast.success(response.data.msg);
          this.$emit('bugCriado', this.csuID);
          this.$router.push(
            `/projects/${idProjeto}/CasoUso/${this.csuID}/Bugs`,
          );
        } catch (error) {
          console.log(error);
          this.$toast.error(error.response.data.msg);
        } finally {
          this.isLoading = false;
          this.dialog = false;
          this.limpaFormulario();
        }
      }
    },

    /**
     * Método auxiliar para limpar os campos do formulário após o seu envio.
     */
    limpaFormulario() {
      this.descricao = '';
      this.observacao = '';
      // eslint-disable-next-line camelcase
      this.tipo_bug = '';
      this.gravidade = '';
    },
  },
};
</script>
